"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Map as MapLibreMap, MapOptions } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { isProfileMapEnabled, resolveProfileMapStyleUrl } from "./profileMapConfig";

const defaultBasemapVectorSourceId = "openmaptiles";

export type ProfileMapCenter = Readonly<{
  latitude: number;
  longitude: number;
  displayLabel?: string;
}>;

export type ProfileMapLabels = Readonly<{
  mapUnavailable: string;
  mapUnavailableDetail: string;
}>;

export type ProfileMapFitBounds = Readonly<{
  southWest: readonly [number, number];
  northEast: readonly [number, number];
}>;

export type ProfileMapLibreModule = Readonly<{
  Map: new (options: MapOptions) => MapLibreMap;
  NavigationControl?: new (options?: Readonly<{
    showCompass?: boolean;
    showZoom?: boolean;
    visualizePitch?: boolean;
  }>) => {
    onAdd: (map: MapLibreMap) => HTMLElement;
    onRemove: (map: MapLibreMap) => void;
  };
  supported?: () => boolean;
}>;

export type ProfileMapRenderOverlayState = Readonly<{
  mapFailed: boolean;
  mapReady: boolean;
  shouldLoadMap: boolean;
}>;

export type ProfileMapLibreSurfaceProps = Readonly<{
  /** Dedicated map workspaces can start focused with zoom enabled once ready. */
  autoFocus?: boolean;
  bindMap?: (map: MapLibreMap) => void | (() => void);
  center: ProfileMapCenter;
  canvasClassName?: string;
  className?: string;
  componentInstance?: string;
  fitBounds?: ProfileMapFitBounds;
  interactive?: boolean;
  labels: ProfileMapLabels;
  mapLocale?: Readonly<Record<string, string>>;
  mapEnabled?: boolean;
  maplibreLoader?: () => Promise<ProfileMapLibreModule>;
  renderOverlay?: (state: ProfileMapRenderOverlayState) => ReactNode;
  styleUrl?: string;
  updateLayers: (map: MapLibreMap, maplibreModule: ProfileMapLibreModule) => void;
}>;

function canObserveViewport(): boolean {
  return typeof window !== "undefined" && "IntersectionObserver" in window;
}

function isElementNearViewport(node: HTMLElement, rootMarginPixels = 160): boolean {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return (
    rect.bottom >= -rootMarginPixels
    && rect.right >= -rootMarginPixels
    && rect.top <= viewportHeight + rootMarginPixels
    && rect.left <= viewportWidth + rootMarginPixels
  );
}

function ProfileMapLibreFallback({ labels }: Readonly<{ labels: ProfileMapLabels }>) {
  return (
    <div className="profile-map-surface profile-map-surface--fallback">
      <strong>{labels.mapUnavailable}</strong>
      <span>{labels.mapUnavailableDetail}</span>
    </div>
  );
}

async function loadProfileMapLibre(): Promise<ProfileMapLibreModule> {
  const maplibregl = await import("maplibre-gl");
  const module = (maplibregl.default ?? maplibregl) as unknown as ProfileMapLibreModule & {
    NavigationControl?: ProfileMapLibreModule["NavigationControl"];
  };
  const namespace = maplibregl as unknown as {
    NavigationControl?: ProfileMapLibreModule["NavigationControl"];
  };
  return {
    Map: module.Map,
    NavigationControl: module.NavigationControl ?? namespace.NavigationControl,
    supported: module.supported,
  };
}

function isBasemapReady(map: MapLibreMap): boolean {
  try {
    const mapWithStyleState = map as MapLibreMap & Readonly<{
      getStyle?: () => Readonly<{ sources?: Readonly<Record<string, unknown>> }> | undefined;
      isStyleLoaded?: () => boolean;
    }>;
    const styleLoaded = typeof mapWithStyleState.isStyleLoaded === "function"
      ? mapWithStyleState.isStyleLoaded() === true
      : true;
    if (!styleLoaded || !map.areTilesLoaded()) {
      return false;
    }
    const styleSources = typeof mapWithStyleState.getStyle === "function"
      ? mapWithStyleState.getStyle()?.sources
      : undefined;
    const shouldCheckDefaultSource = !styleSources
      || Object.prototype.hasOwnProperty.call(styleSources, defaultBasemapVectorSourceId);
    return !shouldCheckDefaultSource || map.isSourceLoaded(defaultBasemapVectorSourceId);
  } catch {
    return false;
  }
}

function resizeMapForContainer(map: MapLibreMap, node: HTMLElement): void {
  const rect = node.getBoundingClientRect();
  const width = node.clientWidth || rect.width;
  const height = node.clientHeight || rect.height;
  if (width <= 0 || height <= 0) {
    return;
  }
  map.resize();
  (map as MapLibreMap & Readonly<{ triggerRepaint?: () => void }>).triggerRepaint?.();
}

function applyFitBounds(map: MapLibreMap, fitBounds: ProfileMapFitBounds): void {
  map.fitBounds(
    [
      [fitBounds.southWest[0], fitBounds.southWest[1]],
      [fitBounds.northEast[0], fitBounds.northEast[1]],
    ],
    {
      animate: false,
      duration: 0,
      maxZoom: 14,
      padding: 20,
    },
  );
}

export function ProfileMapLibreSurface({
  autoFocus = false,
  bindMap,
  center,
  canvasClassName = "profile-map-surface__canvas",
  className,
  componentInstance,
  fitBounds,
  interactive = false,
  labels,
  mapLocale,
  mapEnabled: mapEnabledOverride,
  maplibreLoader,
  renderOverlay,
  styleUrl,
  updateLayers,
}: ProfileMapLibreSurfaceProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const maplibreModuleRef = useRef<ProfileMapLibreModule | null>(null);
  const updateLayersRef = useRef(updateLayers);
  const fitBoundsRef = useRef(fitBounds);
  const bindMapRef = useRef(bindMap);
  const bindMapCleanupRef = useRef<void | (() => void)>(undefined);
  bindMapRef.current = bindMap;
  const [mapFailed, setMapFailed] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [shouldLoadMap, setShouldLoadMap] = useState(() => !canObserveViewport());
  /** Embedded maps wait for engagement; dedicated workspaces opt in on ready. */
  const [zoomInteraction, setZoomInteraction] = useState<"idle" | "active">("idle");
  const mapLocaleRef = useRef(mapLocale);
  mapLocaleRef.current = mapLocale;
  const mapLocaleSignature = JSON.stringify(mapLocale ?? {});
  const mapEnabled = useMemo(
    () => mapEnabledOverride ?? isProfileMapEnabled(),
    [mapEnabledOverride],
  );
  const loadMapLibre = useMemo(
    () => maplibreLoader ?? loadProfileMapLibre,
    [maplibreLoader],
  );
  const resolvedStyleUrl = useMemo(() => resolveProfileMapStyleUrl(styleUrl), [styleUrl]);

  updateLayersRef.current = updateLayers;
  fitBoundsRef.current = fitBounds;

  useEffect(() => {
    if (!mapEnabled || shouldLoadMap) {
      return undefined;
    }
    const node = containerRef.current;
    if (!node || !canObserveViewport()) {
      setShouldLoadMap(true);
      return undefined;
    }

    if (isElementNearViewport(node)) {
      setShouldLoadMap(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
        setShouldLoadMap(true);
        observer.disconnect();
      }
    }, { rootMargin: "160px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mapEnabled, shouldLoadMap]);

  useEffect(() => {
    if (!mapEnabled || !shouldLoadMap) {
      return undefined;
    }

    let disposed = false;
    setMapFailed(false);
    setMapReady(false);

    async function createMap() {
      if (!containerRef.current || mapRef.current) {
        return;
      }
      try {
        const maplibre = await loadMapLibre();
        if (disposed || !containerRef.current) {
          return;
        }
        maplibreModuleRef.current = maplibre;
        const supported = maplibre.supported;
        if (typeof supported === "function" && !supported()) {
          setMapFailed(true);
          return;
        }
        const map = new maplibre.Map({
          // Attribution must stay visible (map provider ToS + platform rule:
          // 07-security-compliance-and-governance-rules). Non-compact keeps the
          // "© OpenMapTiles / OpenStreetMap" credit shown; do not re-collapse it.
          attributionControl: { compact: false },
          center: [center.longitude, center.latitude],
          container: containerRef.current,
          interactive,
          locale: mapLocaleRef.current ? { ...mapLocaleRef.current } : undefined,
          pitchWithRotate: false,
          style: resolvedStyleUrl,
          zoom: 9,
        });
        mapRef.current = map;
        if (interactive) {
          // Gestures are activated by the ready-time interaction effect below.
          map.scrollZoom.disable();
          map.touchZoomRotate.disable();
        }
        if (interactive && typeof maplibre.NavigationControl === "function") {
          map.addControl(
            new maplibre.NavigationControl({ showCompass: false, showZoom: true }),
            "top-right",
          );
        }
        const resizeMap = () => {
          if (!disposed) {
            resizeMapForContainer(map, containerRef.current ?? map.getContainer());
          }
        };
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(resizeMap);
        } else {
          resizeMap();
        }
        let basemapReady = false;
        const markBasemapReady = () => {
          if (disposed || basemapReady || !isBasemapReady(map)) {
            return;
          }
          basemapReady = true;
          setMapFailed(false);
          setMapReady(true);
        };
        const scheduleReadyCheck = () => {
          if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(markBasemapReady);
          } else {
            markBasemapReady();
          }
        };
        const applyLayersAndBounds = () => {
          if (disposed || !maplibreModuleRef.current) {
            return;
          }
          updateLayersRef.current(map, maplibreModuleRef.current);
          const nextFitBounds = fitBoundsRef.current;
          if (nextFitBounds) {
            resizeMapForContainer(map, containerRef.current ?? map.getContainer());
            applyFitBounds(map, nextFitBounds);
          }
        };
        map.once("load", () => {
          if (!disposed) {
            resizeMapForContainer(map, containerRef.current ?? map.getContainer());
            applyLayersAndBounds();
            bindMapCleanupRef.current?.();
            bindMapCleanupRef.current = bindMapRef.current?.(map);
            setMapFailed(false);
            setMapReady(true);
            scheduleReadyCheck();
          }
        });
        map.on("render", () => {
          markBasemapReady();
        });
        map.on("idle", () => {
          markBasemapReady();
        });
        map.on("sourcedata", (event: unknown) => {
          const sourceEvent = event as Readonly<{
            isSourceLoaded?: boolean;
            sourceId?: string;
          }>;
          if (
            sourceEvent.sourceId === defaultBasemapVectorSourceId
            && sourceEvent.isSourceLoaded
          ) {
            scheduleReadyCheck();
          }
        });
        map.on("error", () => {
          scheduleReadyCheck();
        });
      } catch {
        if (!disposed) {
          setMapFailed(true);
        }
      }
    }

    void createMap();

    return () => {
      disposed = true;
      setMapReady(false);
      bindMapCleanupRef.current?.();
      bindMapCleanupRef.current = undefined;
      mapRef.current?.remove();
      mapRef.current = null;
      maplibreModuleRef.current = null;
    };
  }, [center.latitude, center.longitude, interactive, loadMapLibre, mapEnabled, mapLocaleSignature, resolvedStyleUrl, shouldLoadMap]);

  useEffect(() => {
    const map = mapRef.current;
    const surface = containerRef.current?.parentElement;
    if (!interactive || !mapReady || !map || !(surface instanceof HTMLElement)) {
      return undefined;
    }

    // Embedded maps preserve page scrolling until explicitly engaged.
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
    setZoomInteraction("idle");

    const activateZoomInteraction = () => {
      map.scrollZoom.enable();
      map.touchZoomRotate.enable();
      setZoomInteraction("active");
    };
    const deactivateZoomInteraction = () => {
      map.scrollZoom.disable();
      map.touchZoomRotate.disable();
      setZoomInteraction("idle");
    };

    const onSurfaceEnter = () => {
      if (autoFocus) activateZoomInteraction();
    };
    const onSurfacePointerDown = () => {
      activateZoomInteraction();
    };
    const onDocumentPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && surface.contains(event.target)) {
        return;
      }
      deactivateZoomInteraction();
    };
    const onSurfacePointerLeave = () => {
      deactivateZoomInteraction();
    };
    const onPageScroll = () => {
      deactivateZoomInteraction();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        deactivateZoomInteraction();
      }
    };

    surface.addEventListener("pointerenter", onSurfaceEnter);
    surface.addEventListener("focusin", onSurfaceEnter);
    surface.addEventListener("pointerdown", onSurfacePointerDown);
    surface.addEventListener("pointerleave", onSurfacePointerLeave);
    document.addEventListener("pointerdown", onDocumentPointerDown);
    document.addEventListener("scroll", onPageScroll, { capture: true, passive: true });
    window.addEventListener("scroll", onPageScroll, { passive: true });
    document.addEventListener("keydown", onKeyDown);

    if (autoFocus) {
      activateZoomInteraction();
      const focusedElement = document.activeElement;
      // Do not interrupt someone who started using search while tiles loaded.
      if (!focusedElement || focusedElement === document.body || surface.contains(focusedElement)) {
        map.getCanvas().focus({ preventScroll: true });
      }
    }

    return () => {
      surface.removeEventListener("pointerenter", onSurfaceEnter);
      surface.removeEventListener("focusin", onSurfaceEnter);
      surface.removeEventListener("pointerdown", onSurfacePointerDown);
      surface.removeEventListener("pointerleave", onSurfacePointerLeave);
      document.removeEventListener("pointerdown", onDocumentPointerDown);
      document.removeEventListener("scroll", onPageScroll, { capture: true });
      window.removeEventListener("scroll", onPageScroll);
      document.removeEventListener("keydown", onKeyDown);
      map.scrollZoom.disable();
      map.touchZoomRotate.disable();
      setZoomInteraction("idle");
    };
  }, [autoFocus, interactive, mapReady]);

  useEffect(() => {
    if (!mapEnabled || !shouldLoadMap || typeof window.ResizeObserver !== "function") {
      return undefined;
    }
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }
    const observer = new window.ResizeObserver(() => {
      const map = mapRef.current;
      if (map) {
        resizeMapForContainer(map, node);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mapEnabled, shouldLoadMap]);

  useEffect(() => {
    const map = mapRef.current;
    const maplibreModule = maplibreModuleRef.current;
    if (!map || !maplibreModule || !map.loaded()) {
      return;
    }
    updateLayersRef.current(map, maplibreModule);
    bindMapCleanupRef.current?.();
    bindMapCleanupRef.current = bindMapRef.current?.(map);
    if (fitBounds) {
      resizeMapForContainer(map, map.getContainer());
      applyFitBounds(map, fitBounds);
    }
  }, [bindMap, fitBounds, updateLayers]);

  if (!mapEnabled) {
    return <ProfileMapLibreFallback labels={labels} />;
  }

  return (
    <div
      className={[
        "profile-map-surface",
        interactive && zoomInteraction === "active" ? "profile-map-surface--zoom-active" : null,
        className,
      ].filter(Boolean).join(" ")}
      aria-label={center.displayLabel}
      data-basemap-ready={mapReady ? "true" : "false"}
      data-component-instance={componentInstance}
      data-map-failed={mapFailed ? "true" : "false"}
      data-zoom-interaction={interactive ? zoomInteraction : undefined}
    >
      <div
        ref={containerRef}
        className={canvasClassName}
        data-hidden={mapFailed ? "true" : undefined}
      />
      {!shouldLoadMap || (!mapReady && !mapFailed) ? (
        <div className="profile-map-surface__loading" role="status">
          {center.displayLabel ? `Map centered on ${center.displayLabel}` : "Map loading"}
        </div>
      ) : null}
      {mapFailed ? (
        <span className="profile-map-surface__status">{labels.mapUnavailable}</span>
      ) : null}
      {renderOverlay?.({ mapFailed, mapReady, shouldLoadMap })}
    </div>
  );
}
